import { CommonModule } from '@angular/common';
import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, Subject, debounceTime } from 'rxjs';

import { GameBase } from '../../games/game-base';
import { GameFactory } from '../../games/game-factory.service';
import { GameType } from '../../models/communication/app-enums.model';
import { Game, HistoryGame, InitialTableState } from '../../models/communication/app-state.model';
import { CardValue } from '../../models/communication/cards.enum';
import { CardPosition, HandValueKey, Outcome } from '../../models/communication/games.model';
import { GameSwitchComponent } from '../game-switch/game-switch.component';
import { PaginationComponent } from '../pagination/pagination.component';

export interface HistoryGameViewModel {
    enabled: boolean;
    gameNumber: number;
    gameType: GameType;
    displayName: string;
    timestamp: number;
    gameInstance: GameBase;
    index: number;
    isVoid: boolean;
}

/**
 * Displays a history of games and enables navigating through them
 * using the pagination component and a horizontal slider.
 *
 * It uses an intersection observer to track and toggle only
 * the games that are currently visible in the viewport.
 */
@Component({
    selector: 'app-history',
    imports: [CommonModule, PaginationComponent, GameSwitchComponent],
    templateUrl: './history.component.html',
    styleUrl: './history.component.scss'
})
export class HistoryComponent implements OnInit, OnDestroy, AfterViewInit, OnChanges {
    private static readonly itemWidthPx = 1312; // 82 rem
    private static readonly itemHeightPx = 688; // 43 rem
    private static readonly itemGapPx = 88; // 5.5 rem
    private static readonly fontSizeEm = 0.6;

    private static readonly observerTargetSelector = '.observer-target';
    private static readonly observerTargetIndexDataAttr = 'data-target-index';

    private static readonly scrollDebounceAmountMs = 100;
    private static readonly smoothScrollEnabled: boolean = false;
    private static readonly minGamesToShowSlider = 2;

    @ViewChild('gamesContainer') containerElRef!: ElementRef<HTMLElement>;

    @Input()
    currentGame!: Game;

    @Input()
    historyGames: HistoryGame[] = [];
    @Input() loading = true;

    @Input()
    scaleFactor = 1;

    @Output()
    readonly closeClicked = new EventEmitter();

    protected games: HistoryGameViewModel[] = [];

    protected pageIndex = 0;
    protected sliderEnabled = false;
    protected devMode = false;

    private interactionObserver!: IntersectionObserver;
    private containerEl!: HTMLElement;

    private readonly scrollSubject = new Subject<number>();

    constructor(private readonly gameFactory: GameFactory) {
        let scrollObservable: Observable<number> = this.scrollSubject.pipe(takeUntilDestroyed());
        if (HistoryComponent.smoothScrollEnabled) {
            scrollObservable = scrollObservable.pipe(debounceTime(HistoryComponent.scrollDebounceAmountMs));
        }

        scrollObservable.subscribe(this.onScrollEnd.bind(this));
    }

    /**
     * Returns a visual representation of the enabled status of games,
     * toggled by the intersection observer. Useful for debugging when
     * `devMode` is enabled.
     */
    get enabledGamesVisualization(): string {
        return this.games.map((game) => (game.enabled ? '●' : '○')).join('');
    }

    ngOnInit(): void {
        this.initGamesViewModel(this.historyGames);
        this.initSlider();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if ('historyGames' in changes) {
            this.initGamesViewModel(this.historyGames);
            this.initSlider();
            this.games = this.games.map((game) => ({
                ...game,
                enabled: true
            }));
        }
    }

    ngAfterViewInit(): void {
        this.initContainerEl();
        this.initIntersectionObserver();
    }

    ngOnDestroy(): void {
        this.interactionObserver.disconnect();
    }

    protected updatePageIndex(pageIndex: number): void {
        this.containerEl.scrollTo({ left: this.indexToScroll(pageIndex) });
    }

    protected onScroll(event: Event): void {
        this.scrollSubject.next((event.target as HTMLElement).scrollLeft);
    }

    protected onSlide(event: Event): void {
        const scrollPosition = this.indexToScroll((event.target as HTMLInputElement).valueAsNumber);
        this.containerEl.scrollTo({ left: scrollPosition });

        this.scrollSubject.next(scrollPosition);
    }

    private onScrollEnd(value: number): void {
        this.pageIndex = this.scrollToIndex(value);
    }

    private mapGameTypeToName(gameType: GameType): string {
        switch (gameType) {
            case GameType.DragonTiger:
                return 'Dragon Tiger';
            case GameType.Baccarat:
                return 'Baccarat';
            case GameType.ThreeHeadedDragon:
                return 'Three Headed Dragon';
            case GameType.OpenBaccarat:
                return 'Open Baccarat';
            case GameType.OpenDragonTiger:
                return 'Open Dragon Tiger';
            default:
                return gameType;
        }
    }

    private initGamesViewModel(historyGames: HistoryGame[]): void {
        this.games = historyGames
            .map((historyGame, index) => {
                const { gameType, openTime, status, state } = historyGame;

                const base: Omit<HistoryGameViewModel, 'gameInstance' | 'gameNumber' | 'isVoid'> = {
                    enabled: false,
                    gameType,
                    displayName: this.mapGameTypeToName(gameType),
                    timestamp: openTime,
                    index
                };

                const buildGame = (
                    cards: Array<{ card: CardValue; index: CardPosition }>,
                    outcome?: Outcome,
                    handValue?: number,
                    handValues?: Record<string, number>
                ): GameBase =>
                    this.initGame(gameType, { cards, handValue: this.mapHandValues(handValues) } as InitialTableState, outcome, handValue);

                switch (status) {
                    case 'Closed':
                        return {
                            ...base,
                            gameNumber: state.gameNumber,
                            gameInstance: buildGame(state.cards, state.winner[0], state.handValue, state.handValues),
                            isVoid: false
                        } as HistoryGameViewModel;

                    case 'Void':
                        return {
                            ...base,
                            gameNumber: state.roundState.roundId,
                            gameInstance: buildGame(state.tableState?.cards ?? []),
                            isVoid: true
                        } as HistoryGameViewModel;

                    default:
                        // In the future we can display Open(currently in progress) games as well
                        return null;
                }
            })
            .filter((g): g is HistoryGameViewModel => g !== null)
            .reverse();
    }

    /**
     * Maps history hand value names (`Banker`, `RedDragon`…) to the
     * `HandValueKey` format (`bankerHandValue`, `redDragonHandValue`…)
     * used by the game view model.
     */
    private mapHandValues(handValues?: Record<string, number>): Partial<Record<HandValueKey, number>> | undefined {
        if (!handValues) {
            return undefined;
        }

        return Object.fromEntries(
            Object.entries(handValues).map(([name, value]) => [`${name.charAt(0).toLowerCase()}${name.slice(1)}HandValue`, value])
        );
    }

    private initGame(gameType: GameType, initialData: InitialTableState, outcome?: Outcome, handValue?: number): GameBase {
        const game = this.gameFactory.create(gameType, initialData);

        if (outcome !== undefined && handValue !== undefined) {
            game.handleOutcome({ outcome }, handValue);
        }

        return game;
    }

    private initSlider(): void {
        this.sliderEnabled = this.games.length > 1 && this.games.length >= HistoryComponent.minGamesToShowSlider;
    }

    private initContainerEl(): void {
        this.containerEl = this.containerElRef.nativeElement;
        this.containerEl.style.setProperty('--item-width', `${HistoryComponent.itemWidthPx * this.scaleFactor}px`);
        this.containerEl.style.setProperty('--item-height', `${HistoryComponent.itemHeightPx * this.scaleFactor}px`);
        this.containerEl.style.setProperty('--item-gap', `${HistoryComponent.itemGapPx * this.scaleFactor}px`);
        this.containerEl.style.setProperty('--font-size', `${HistoryComponent.fontSizeEm * this.scaleFactor}em`);

        if (HistoryComponent.smoothScrollEnabled) {
            this.containerEl.style.scrollBehavior = 'smooth';
        }
    }

    private initIntersectionObserver(): void {
        this.interactionObserver = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    const index = Number(entry.target.getAttribute(HistoryComponent.observerTargetIndexDataAttr));
                    this.games[index].enabled = entry.isIntersecting;
                }
            },
            {
                root: this.containerEl
            }
        );

        const targets = this.containerEl.querySelectorAll(HistoryComponent.observerTargetSelector);
        for (const target of Array.from(targets)) {
            this.interactionObserver.observe(target);
        }
    }

    private scrollToIndex(scrollValue: number): number {
        return Math.ceil(scrollValue / (HistoryComponent.itemWidthPx * this.scaleFactor + HistoryComponent.itemGapPx * this.scaleFactor));
    }

    private indexToScroll(index: number): number {
        return (HistoryComponent.itemWidthPx * this.scaleFactor + HistoryComponent.itemGapPx * this.scaleFactor) * index;
    }
}
